import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Clock, Star, Video, MessageCircle, Award, TrendingUp, Play, Phone, Mic, MicOff, VideoOff, Settings, UserPlus, UserMinus, X, Send } from 'lucide-react';
import { ExpertSession, ExpertProfile } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useChat } from '../hooks/useChat';
import { useProfile } from '../hooks/useProfile';
import { useExpertSessions } from '../hooks/useExpertSessions';
import { useDailyRoom } from '../hooks/useDailyRoom';
import { showToast } from '../utils/toast';

interface ExpertSpotlightProps {
  sessions: ExpertSession[];
  experts: ExpertProfile[];
  onJoinSession: (sessionId: string) => void;
  onFollowExpert: (expertId: string) => void;
  onAskQuestion: (expertId: string, question: string) => void;
}

const ExpertSpotlight: React.FC<ExpertSpotlightProps> = ({
  sessions,
  experts,
  onJoinSession,
  onFollowExpert,
  onAskQuestion
}) => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedExpert, setSelectedExpert] = useState<ExpertProfile | null>(null);
  const [showAskQuestion, setShowAskQuestion] = useState(false);
  const [question, setQuestion] = useState('');
  const [showLiveStream, setShowLiveStream] = useState<string | null>(null);
  const [followedExperts, setFollowedExperts] = useState<Set<string>>(new Set());
  const [liveMessage, setLiveMessage] = useState('');
  
  const { user } = useAuth();
  const { startConversation } = useChat();
  const { profile, addFollowingExpert, removeFollowingExpert } = useProfile();
  const { 
    sessions: realSessions, 
    reserveSession, 
    cancelReservation, 
    isSessionReserved 
  } = useExpertSessions();
  
  const { 
    containerRef, 
    joinRoom, 
    leaveRoom, 
    participants, 
    chatMessages, 
    isJoined, 
    isMicOn, 
    isCameraOn, 
    toggleMic, 
    toggleCamera, 
    sendChatMessage 
  } = useDailyRoom();

  // Use the real sessions if available, otherwise use the mock sessions
  const allSessions = realSessions.length > 0 ? realSessions : sessions;
  
  const upcomingSessions = allSessions.filter(s => s.status === 'upcoming');
  const liveSessions = allSessions.filter(s => s.status === 'live');
  const completedSessions = allSessions.filter(s => s.status === 'completed');

  // Load followed experts and reserved sessions from profile
  useEffect(() => {
    if (profile) {
      // Set followed experts
      if (profile.following_experts && profile.following_experts.length > 0) {
        setFollowedExperts(new Set(profile.following_experts));
      }
    }
  }, [profile]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return 'bg-red-500 text-white dark:bg-red-600 dark:text-white animate-pulse';
      case 'upcoming': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100';
    }
  };

  const handleReserveSpot = (sessionId: string, sessionTitle: string) => {
    if (!user) {
      showToast.auth.authRequired('Please sign in to reserve spots');
      return;
    }

    reserveSession(sessionId)
      .then(() => {
        showToast.expert.sessionJoined(sessionTitle);
      })
      .catch(error => {
        console.error('Error reserving session:', error);
      });
  };

  const handleCancelReservation = (sessionId: string) => {
    if (!user) {
      showToast.auth.authRequired('Please sign in to manage reservations');
      return;
    }

    cancelReservation(sessionId)
      .then(() => {
        showToast.system.success('Reservation cancelled');
      })
      .catch(error => {
        console.error('Error cancelling reservation:', error);
      });
  };

  const handleFollowExpert = (expertUserId: string, expertName: string) => {
    if (!user) {
      showToast.auth.authRequired('Please sign in to follow experts');
      return;
    }

    const isCurrentlyFollowing = followedExperts.has(expertUserId);
    
    if (isCurrentlyFollowing) {
      setFollowedExperts(prev => {
        const newSet = new Set(prev);
        newSet.delete(expertUserId);
        return newSet;
      });
      showToast.community.unfollowSuccess(expertName);
      removeFollowingExpert(expertUserId);
    } else {
      setFollowedExperts(prev => new Set([...prev, expertUserId]));
      showToast.community.followSuccess(expertName);
      addFollowingExpert(expertUserId);
      onFollowExpert(expertUserId);
    }
  };

  const handleJoinLiveSession = async (session: ExpertSession) => {
    if (!user) {
      showToast.auth.authRequired('Please sign in to join live sessions');
      return;
    }
    
    try {
      setShowLiveStream(session.id);
      onJoinSession(session.id);
      
      // If the session has a Daily room URL, join it
      if (session.dailyRoomUrl) {
        // Wait for the container ref to be available
        setTimeout(() => {
          if (containerRef.current) {
            joinRoom(
              session.dailyRoomUrl!, 
              user.user_metadata?.full_name || 'Anonymous',
              containerRef.current
            );
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error joining live session:', error);
      showToast.system.error('Failed to join live session');
    }
  };

  const handleAskExpertQuestion = async (expertUserId: string, expertName: string) => {
    if (!user) {
      showToast.auth.authRequired('Please sign in to message experts');
      return;
    }

    try {
      // Start a conversation with the expert using their userId
      const conversationId = await startConversation(expertUserId, expertName, true);
      showToast.chat.conversationStarted(expertName);
      
      // Close the ask question modal
      setShowAskQuestion(false);
      setQuestion('');
      setSelectedExpert(null);
    } catch (error) {
      showToast.system.error('Failed to start conversation');
    }
  };

  const handleSendLiveMessage = () => {
    if (liveMessage.trim()) {
      sendChatMessage(liveMessage);
      setLiveMessage('');
    }
  };

  // Handle backdrop clicks for modal dismissal
  const handleModalBackdropClick = (e: React.MouseEvent, closeModal: () => void) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const SessionCard = ({ session }: { session: ExpertSession }) => {
    const isReserved = isSessionReserved(session.id);
    
    return (
      <div className="card-interactive p-6 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className={`badge-base ${getStatusColor(session.status)}`}>
              {session.status === 'live' && <span className="mr-1">🔴</span>}
              {session.status.toUpperCase()}
            </span>
            <span className="text-gray-500 dark:text-gray-400 text-sm">{session.category}</span>
          </div>
          {session.price > 0 && (
            <span className="badge-warning">
              ${session.price}
            </span>
          )}
        </div>
        
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{session.title}</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">{session.description}</p>
        
        <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
          <span className="flex items-center space-x-1">
            <Calendar size={14} />
            <span>{session.scheduledDate}</span>
          </span>
          <span className="flex items-center space-x-1">
            <Clock size={14} />
            <span>{session.duration} min</span>
          </span>
          <span className="flex items-center space-x-1">
            <Users size={14} />
            <span>{session.currentAttendees}/{session.maxAttendees}</span>
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="avatar-sm">
              {session.expertName.charAt(0)}
            </div>
            <span className="font-medium text-gray-700 dark:text-gray-300">{session.expertName}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            {isReserved && (
              <span className="badge-info">
                ✓ Reserved
              </span>
            )}
            <button
              onClick={() => {
                if (session.status === 'live') {
                  handleJoinLiveSession(session);
                } else if (session.status === 'upcoming') {
                  if (isReserved) {
                    handleCancelReservation(session.id);
                  } else {
                    handleReserveSpot(session.id, session.title);
                  }
                } else {
                  onJoinSession(session.id);
                }
              }}
              disabled={session.currentAttendees >= session.maxAttendees && session.status !== 'live'}
              className={`btn-sm ${
                session.status === 'live'
                  ? 'btn-error'
                  : session.status === 'upcoming'
                  ? isReserved 
                    ? 'btn-secondary'
                    : 'btn-primary'
                  : 'btn-ghost'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {session.status === 'live' ? (
                <span className="flex items-center space-x-1">
                  <Video size={16} />
                  <span>Join Live</span>
                </span>
              ) : session.status === 'upcoming' ? (
                isReserved ? 'Cancel Reservation' : 'Reserve Spot'
              ) : (
                <span className="flex items-center space-x-1">
                  <Play size={16} />
                  <span>Watch Replay</span>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ExpertCard = ({ expert }: { expert: ExpertProfile }) => {
    const isFollowing = followedExperts.has(expert.userId);
    
    return (
      <div className="card-interactive p-6 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-start space-x-4 mb-4">
          <div className="avatar-xl">
            {expert.userId.charAt(0)}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Dr. Expert {expert.id}</h3>
              {expert.verified && (
                <span className="badge-info">
                  ✓ Verified
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {expert.specialization.slice(0, 2).map((spec, index) => (
                <span key={index} className="badge-secondary">
                  {spec}
                </span>
              ))}
              {expert.specialization.length > 2 && (
                <span className="badge-base bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  +{expert.specialization.length - 2} more
                </span>
              )}
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2">{expert.bio || 'Healthcare professional dedicated to improving health outcomes in Ghana.'}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-4 text-center">
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{expert.followers}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Followers</p>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{expert.totalAnswers}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Answers</p>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{expert.helpfulVotes}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Helpful</p>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => handleFollowExpert(expert.userId, `Dr. Expert ${expert.id}`)}
            className={`btn-md flex-1 flex items-center justify-center space-x-1 ${
              isFollowing
                ? 'btn-ghost'
                : 'btn-primary'
            }`}
          >
            {isFollowing ? <UserMinus size={16} /> : <UserPlus size={16} />}
            <span>{isFollowing ? 'Following' : 'Follow'}</span>
          </button>
          <button
            onClick={() => {
              setSelectedExpert(expert);
              setShowAskQuestion(true);
            }}
            className="btn-primary flex-1 flex items-center justify-center space-x-1"
          >
            <MessageCircle size={16} />
            <span>Message</span>
          </button>
        </div>
      </div>
    );
  };

  const LiveStreamModal = ({ sessionId }: { sessionId: string }) => {
    const session = allSessions.find(s => s.id === sessionId);
    if (!session) return null;

    return (
      <div 
        className="fixed inset-0 bg-black z-50 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-900 text-white p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <h3 className="font-semibold">{session.title}</h3>
            <span className="badge-error">LIVE</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm">{participants.length} viewers</span>
            <button
              onClick={() => {
                leaveRoom();
                setShowLiveStream(null);
              }}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 flex">
          {/* Main Video Area */}
          <div className="flex-1 bg-gray-900 flex items-center justify-center relative">
            {/* Daily.co container */}
            <div 
              ref={containerRef} 
              className="w-full h-full"
            ></div>

            {/* Fallback if Daily.co isn't loaded yet */}
            {!isJoined && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="avatar-xl mx-auto mb-4">
                    {session.expertName.charAt(0)}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{session.expertName}</h3>
                  <p className="text-gray-300">{session.description}</p>
                  <div className="mt-4">
                    <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-2">Joining session...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Video Controls */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 bg-black/50 rounded-full px-6 py-3">
              <button
                onClick={toggleMic}
                className={`p-2 rounded-full transition-colors ${
                  isMicOn ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-red-600 text-white'
                }`}
              >
                {isMicOn ? <Mic size={20} /> : <MicOff size={20} />}
              </button>
              <button
                onClick={toggleCamera}
                className={`p-2 rounded-full transition-colors ${
                  isCameraOn ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-red-600 text-white'
                }`}
              >
                {isCameraOn ? <Video size={20} /> : <VideoOff size={20} />}
              </button>
              <button className="p-2 rounded-full bg-gray-700 text-white hover:bg-gray-600 transition-colors">
                <Settings size={20} />
              </button>
              <button
                onClick={() => {
                  leaveRoom();
                  setShowLiveStream(null);
                }}
                className="p-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                <Phone size={20} />
              </button>
            </div>
          </div>

          {/* Live Chat */}
          <div className="w-80 bg-white dark:bg-gray-800 flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Live Chat</h4>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-custom">
              {chatMessages.length === 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                  <p>Be the first to send a message!</p>
                </div>
              )}
              {chatMessages.map((msg) => (
                <div key={msg.id} className="text-sm">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{msg.sender.name}</span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200">{msg.text}</p>
                </div>
              ))}
            </div>
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={liveMessage}
                  onChange={(e) => setLiveMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendLiveMessage()}
                  placeholder="Type a message..."
                  className="input-base flex-1"
                />
                <button
                  onClick={handleSendLiveMessage}
                  className="btn-primary"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AskQuestionModal = () => (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => handleModalBackdropClick(e, () => setShowAskQuestion(false))}
    >
      <div className="card-base w-full max-w-2xl dark:bg-gray-900 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Message Expert</h3>
          <p className="text-gray-600 dark:text-gray-300 mt-1">Start a private conversation with a verified health expert</p>
        </div>
        
        <div className="p-6">
          {selectedExpert && (
            <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="avatar-sm">
                {selectedExpert.userId.charAt(0)}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Dr. Expert {selectedExpert.id}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedExpert.specialization.join(', ')}</p>
              </div>
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Message</label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={6}
              placeholder="Describe your health concern or question in detail..."
              className="textarea-base"
            />
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <p className="text-blue-800 dark:text-blue-300 text-sm">
              <strong>Note:</strong> This will start a private chat conversation with the expert. 
              They will be notified and can respond when available.
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowAskQuestion(false);
                setQuestion('');
                setSelectedExpert(null);
              }}
              className="btn-ghost"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (selectedExpert && question.trim()) {
                  handleAskExpertQuestion(selectedExpert.userId, `Dr. Expert ${selectedExpert.id}`);
                }
              }}
              disabled={!question.trim()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Start Conversation
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Header */}
      <div className="glass border-b border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Expert Directory</h1>
              <p className="text-gray-600 dark:text-gray-300">Connect with verified health professionals</p>
            </div>
            <div className="flex items-center space-x-4">
              {liveSessions.length > 0 && (
                <div className="flex items-center space-x-2 badge-error px-3 py-2 rounded-lg">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">{liveSessions.length} Live Now</span>
                </div>
              )}
              <button
                onClick={() => {
                  if (!user) {
                    showToast.auth.authRequired('Please sign in to message experts');
                    return;
                  }
                  setShowAskQuestion(true);
                }}
                className="btn-primary flex items-center space-x-2"
              >
                <MessageCircle size={20} />
                <span>Message Expert</span>
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex space-x-1 glass rounded-2xl p-2 border border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
            {[
              { id: 'upcoming', label: 'Upcoming Sessions', count: upcomingSessions.length },
              { id: 'live', label: 'Live Now', count: liveSessions.length },
              { id: 'experts', label: 'All Experts', count: experts.length },
              { id: 'completed', label: 'Past Sessions', count: completedSessions.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-gray-700 text-green-700 dark:text-green-400 shadow-md'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                }`}
              >
                <span>{tab.label}</span>
                <span className="badge-base bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'live' && liveSessions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span>Live Sessions</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {liveSessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'upcoming' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingSessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))}
          </div>
        )}

        {activeTab === 'experts' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {experts.map((expert) => (
              <ExpertCard key={expert.id} expert={expert} />
            ))}
          </div>
        )}

        {activeTab === 'completed' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {completedSessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))}
          </div>
        )}

        {((activeTab === 'upcoming' && upcomingSessions.length === 0) ||
          (activeTab === 'live' && liveSessions.length === 0) ||
          (activeTab === 'completed' && completedSessions.length === 0) ||
          (activeTab === 'experts' && experts.length === 0)) && (
          <div className="text-center py-12">
            <Video className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {activeTab === 'live' ? 'No live sessions' : 
               activeTab === 'upcoming' ? 'No upcoming sessions' :
               activeTab === 'experts' ? 'No experts available' :
               'No past sessions'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">Check back later for new content.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAskQuestion && <AskQuestionModal />}
      {showLiveStream && <LiveStreamModal sessionId={showLiveStream} />}
    </div>
  );
};

export default ExpertSpotlight;